/**
 * Event → Cue mapping (plan 08 A2/A3). PURE: no Web Audio types, no timers, no DOM — the whole
 * audio semantics is decided here so it is unit-testable in node. `audio.ts` feeds drained
 * `SimEvent`s plus the current `SimState` snapshot in and plays the returned cues.
 *
 * The volley detector is the ONE exception to the "render is a pure snapshot function" rule (plan
 * 08 timing contract): `sim.shotIndex` is not a once-per-volley signal (several volleys can fire
 * per substep, and a per-frame drain collapses up to 8), so audio collapses any positive jump to
 * exactly one cue per `map()` call and `reset()` re-syncs the latch on restart.
 */
import { enemyThreat, type SimEvent, type SimState } from '../sim/sim';
import { ENEMY_LOOKAHEAD } from '../sim/level';

/** Stereo position in [-1, 1]; gate sides hard-pan ∓0.6, squad-attached cues use sim.squadX. */
export type Pan = number;

export const GATE_PAN = 0.6;

export type BossKind = 'brute' | 'tank' | 'heli';
export type UiSound = 'confirm' | 'deploy' | 'dispatch' | 'tierComplete' | 'operationComplete';

/** A sound the synth knows how to play. One Cue = one (composite) voice, never per bullet. */
export type Cue =
  | { type: 'volley'; tier: number; shown: number; pan: Pan }
  | { type: 'gateTick'; side: 'left' | 'right'; value: number; pan: Pan }
  | { type: 'gatePass'; side: 'left' | 'right'; good: boolean; pan: Pan }
  | { type: 'crateHit'; pan: Pan }
  | { type: 'crateBreak'; pan: Pan }
  | { type: 'metalHit'; pan: Pan }
  | { type: 'metalBreak'; pan: Pan }
  | { type: 'weaponUp'; tier: number }
  | { type: 'enemyKill'; pan: Pan }
  | { type: 'loss'; lost: number; pan: Pan }
  | { type: 'slamTelegraph'; pan: Pan }
  | { type: 'slamImpact'; hit: boolean }
  /** Explosive barrel blast. */
  | { type: 'explosion'; pan: Pan }
  /** Plan 15 P15.3: the tank main gun (bossFire, panned to the tank x at fire) and the shell's
   *  detonation — a BIGGER impact than the brute's slam (synth gives it its own longer voice). */
  | { type: 'cannon'; pan: Pan }
  | { type: 'rocket'; pan: Pan }
  | { type: 'ui'; sound: UiSound }
  | { type: 'shellImpact'; hit: boolean }
  | { type: 'bossHit' }
  | { type: 'won' }
  | { type: 'lost' };

/** Clamp a raw lateral value into the panner range. */
function panClamp(v: number): Pan {
  return Math.max(-1, Math.min(1, Number.isFinite(v) ? v : 0));
}

function gatePan(side: 'left' | 'right'): Pan {
  return side === 'left' ? -GATE_PAN : GATE_PAN;
}

/**
 * Was the passed gate good for the squad? The event carries only the AFTER count, so the rule is
 * read off the gate itself (still present in sim.gates, consumed=true): 'num' adds when its live
 * value > 0, 'mul' (×k, k≥2) always helps while n ≥ 1, 'div' (÷k) always hurts. Simplest rule
 * that matches applyGate's arithmetic exactly.
 */
function gateWasGood(sim: SimState, side: 'left' | 'right', z: number): boolean {
  const gate = sim.gates.find((g) => g.side === side && g.z === z);
  if (!gate) return true; // gate not in state (shouldn't happen): don't play the dissonant stinger
  if (gate.spec.kind === 'mul') return true;
  if (gate.spec.kind === 'div') return false;
  return gate.value > 0;
}

/** Sliding-window rate limiter: at most `max` triggers per 1 s of sim time (boundary can pass 2×
 *  for one second — acceptable for audio, keeps the state to two numbers per key). */
class RateLimit {
  private windowStart = -Infinity;
  private count = 0;
  constructor(private readonly max: number) {}
  allow(t: number): boolean {
    if (t - this.windowStart >= 1) {
      this.windowStart = t;
      this.count = 0;
    }
    if (this.count >= this.max) return false;
    this.count++;
    return true;
  }
  reset(): void {
    this.windowStart = -Infinity;
    this.count = 0;
  }
}

/** enemyKill ≤ 12/s, bossHit ≤ 8/s (plan 08 SFX table): a 48-man volley must not machine-gun voices. */
export const ENEMY_KILL_RATE = 12;
export const BOSS_HIT_RATE = 8;
/** Plan 09: a barricade under 4-column rifle fire takes ≈ 57 hits/s; knock at most 12/s. */
export const OBSTACLE_HIT_RATE = 12;

/**
 * Stateful event→cue translator. One instance per game session; `reset()` is called on restart so
 * the volley latch re-syncs to the fresh `shotIndex` (plan 08 timing contract).
 */
export class CueMapper {
  /** Baseline 0 matches createSim's shotIndex = 0; reset() re-arms it so restart never mutes gunfire. */
  private prevShotIndex = 0;
  private readonly enemyKillLimit = new RateLimit(ENEMY_KILL_RATE);
  private readonly bossHitLimit = new RateLimit(BOSS_HIT_RATE);
  private readonly obstacleHitLimit = new RateLimit(OBSTACLE_HIT_RATE);

  constructor(private readonly bossKind: BossKind = 'brute') {}

  reset(): void {
    this.prevShotIndex = 0;
    this.enemyKillLimit.reset();
    this.bossHitLimit.reset();
    this.obstacleHitLimit.reset();
  }

  /** Translate one frame's drained events (+ snapshot) into cues, in event order. */
  map(events: SimEvent[], sim: SimState): Cue[] {
    const cues: Cue[] = [];
    const squadPan = panClamp(sim.squadX);

    // Volley detector: any positive shotIndex jump collapses to exactly ONE cue per call.
    if (sim.shotIndex - this.prevShotIndex > 0) {
      cues.push({ type: 'volley', tier: sim.weaponTier, shown: Math.max(0, sim.n), pan: squadPan });
    }
    this.prevShotIndex = sim.shotIndex;

    for (const ev of events) {
      switch (ev.type) {
        case 'gateShot':
          cues.push({ type: 'gateTick', side: ev.side, value: ev.value, pan: gatePan(ev.side) });
          break;
        case 'gatePass':
          cues.push({
            type: 'gatePass',
            side: ev.side,
            good: gateWasGood(sim, ev.side, ev.z),
            pan: gatePan(ev.side),
          });
          break;
        case 'crateHit':
          cues.push({ type: 'crateHit', pan: squadPan });
          break;
        case 'crateBreak':
          cues.push({ type: 'crateBreak', pan: squadPan });
          break;
        // Barrel shells and barricades have a metallic impact, panned to the obstacle.
        case 'obstacleHit':
          if (this.obstacleHitLimit.allow(ev.t)) cues.push({ type: 'metalHit', pan: panClamp(ev.x) });
          break;
        case 'obstacleBreak':
          cues.push({ type: 'metalBreak', pan: panClamp(ev.x) });
          if (ev.lost > 0) cues.push({ type: 'loss', lost: ev.lost, pan: squadPan });
          break;
        case 'explosion':
          cues.push({ type: 'explosion', pan: panClamp(ev.x) });
          if (ev.lost > 0) cues.push({ type: 'loss', lost: ev.lost, pan: squadPan });
          break;
        case 'weaponUp':
          cues.push({ type: 'weaponUp', tier: ev.tier });
          break;
        case 'weaponMod':
          // Plan 11: a pip reuses the tier-up sweep, one step higher per pip. A strip is silent: it
          // lands on the slamImpact hit step, and the tracer tint + HUD flash carry it.
          if (ev.gained) cues.push({ type: 'weaponUp', tier: sim.weaponTier + ev.pips });
          break;
        case 'enemyKill':
          if (this.enemyKillLimit.allow(ev.t)) cues.push({ type: 'enemyKill', pan: squadPan });
          break;
        case 'enemyContact':
          // Audit #8: same guard as obstacleBreak/explosion — the sim emits lost 0 when the squad
          // passes the block in the other lane, and the synth clamps lost up to 1, so an
          // unguarded push played a casualty thud on a clean dodge.
          if (ev.lost > 0) cues.push({ type: 'loss', lost: ev.lost, pan: squadPan });
          break;
        case 'hazardHit':
          if (ev.penalty > 0) cues.push({ type: 'loss', lost: ev.penalty, pan: squadPan });
          break;
        case 'bossHit':
          if (this.bossHitLimit.allow(ev.t)) cues.push({ type: 'bossHit' });
          break;
        case 'slamTelegraph':
          cues.push({ type: 'slamTelegraph', pan: panClamp((ev.zone[0] + ev.zone[1]) / 2) });
          break;
        case 'slamImpact':
          cues.push({ type: 'slamImpact', hit: ev.hit });
          break;
        // Plan 15 P15.3: the tank cycle. The cannon pans to the tank x the event carries (the FX
        // origin approximation); the detonation is the slam rule verbatim, so `hit` rides along.
        case 'bossFire':
          cues.push({ type: this.bossKind === 'heli' ? 'rocket' : 'cannon', pan: panClamp(ev.x) });
          break;
        case 'shellImpact':
          cues.push({ type: 'shellImpact', hit: ev.hit });
          break;
        case 'won':
          cues.push({ type: 'won' });
          break;
        case 'lost':
          cues.push({ type: 'lost' });
          break;
      }
    }
    return cues;
  }
}

/** Music intensity layer (plan 08 A3). Pure function of the snapshot. */
export type Intensity = 'march' | 'engage' | 'boss' | 'won' | 'lost';

/**
 * Layer selection: end states win outright, then the boss fight, then any not-yet-engaged enemy
 * block inside the HUD lookahead (same `enemyThreat` the M readout uses, so audio agrees with
 * what the player can see coming). `hazards near` is deliberately NOT included: hazards are
 * one-shot latches and the threat check would need level access — engage is already correct for
 * every shipped level's enemy spacing.
 */
export function intensity(sim: SimState, lookahead = ENEMY_LOOKAHEAD): Intensity {
  if (sim.phase === 'won') return 'won';
  if (sim.phase === 'lost') return 'lost';
  if (sim.phase === 'boss') return 'boss';
  return enemyThreat(sim, lookahead) ? 'engage' : 'march';
}

/** Plan 24 Goal (a): boss health fraction, 1 (full) → 0 (dead). PURE — the ONLY run-state
 *  input the music engine consumes (general density/brightness sonification is Option 4,
 *  a non-goal). audio.ts forwards it to `MusicSequencer.setBossHpFrac` every update;
 *  crossing ≤ 0.5 escalates subsequent boss bars (second timbre step + accent density). */
export function bossTension(sim: SimState): number {
  if (sim.phase !== 'boss') return 1;
  return Math.max(0, Math.min(1, sim.bossMaxHp > 0 ? sim.bossHp / sim.bossMaxHp : 1));
}
